'use client';

import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const STORAGE_KEY = 'sensitivity:hidden';
const CHANGE_EVENT = 'sensitivity:change';
let sessionHidden = false;

function readHidden() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return sessionHidden;
  }
}

function subscribeHidden(onChange: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) onChange();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
}

interface SensitivityContextValue {
  isHidden: boolean;
  toggle: () => void;
  /** Mask a dollar string, e.g. "$1,234.56" → "$*****" */
  mask: (value: string) => string;
}

const SensitivityContext = React.createContext<SensitivityContextValue>({
  isHidden: false,
  toggle: () => {},
  mask: v => v,
});

/**
 * SensitivityProvider — Wraps a tree to enable show/hide dollar amounts.
 *
 * Any child can call `useSensitivity()` to check `isHidden` and use
 * `mask(value)` to obfuscate currency strings.
 */
function SensitivityProvider({ children }: { children: React.ReactNode }) {
  const isHidden = React.useSyncExternalStore(
    subscribeHidden,
    readHidden,
    () => false
  );

  const toggle = React.useCallback(() => {
    sessionHidden = !readHidden();
    try {
      localStorage.setItem(STORAGE_KEY, String(sessionHidden));
    } catch {
      /* Keep the control usable when browser storage is unavailable. */
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  const mask = React.useCallback(
    (value: string) => {
      if (!isHidden) return value;
      // Preserve currency symbol prefix, mask digits
      const match = value.match(/^([^0-9]*)/);
      const prefix = match?.[1] || '';
      return `${prefix}*****`;
    },
    [isHidden]
  );

  const ctx = React.useMemo(
    () => ({ isHidden, toggle, mask }),
    [isHidden, toggle, mask]
  );

  return (
    <SensitivityContext.Provider value={ctx}>
      {children}
    </SensitivityContext.Provider>
  );
}

function useSensitivity() {
  return React.use(SensitivityContext);
}

/**
 * SensitivityToggle — Eye icon button that toggles dollar visibility.
 */
function SensitivityToggle({ className }: { className?: string }) {
  const { isHidden, toggle } = useSensitivity();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={className}
          onClick={toggle}
          aria-label={isHidden ? 'Show amounts' : 'Hide amounts'}
        >
          {isHidden ? (
            <EyeOff className="size-4" />
          ) : (
            <Eye className="size-4" />
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isHidden ? 'Show amounts' : 'Hide amounts'}
      </TooltipContent>
    </Tooltip>
  );
}

export { SensitivityProvider, SensitivityToggle, useSensitivity };
